import type { Paragraph, TextLine, TextRun } from '@/api/types/stowed-element'
import {
  DEFAULT_FONT_FAMILY_ID,
  DEFAULT_FONT_ID,
  DEFAULT_LEADING,
  DEFAULT_POINT_SIZE,
} from '@/lib/text/constants/text-defaults'
import type { ParagraphBlockFontLookup } from '@/lib/text/types/paragraph-block-font-lookup'
import { fillTextLineWireMetrics } from '@/lib/text/fill-text-run-wire-metrics'
import { extractRunStyle, runStyleEquals } from '@/lib/text/run-style'

export function ensureTextRunDefaults(run: TextRun): TextRun {
  return {
    ...run,
    fontId: run.fontId ?? DEFAULT_FONT_ID,
    fontFamilyId: run.fontFamilyId ?? DEFAULT_FONT_FAMILY_ID,
    pointSize: run.pointSize ?? DEFAULT_POINT_SIZE,
    underline: run.underline ?? false,
    strikethrough: run.strikethrough ?? false,
    // yb360 semantics: empty LD is not "auto" — it renders with no leading at all.
    leading: run.leading == null || run.leading === '' ? DEFAULT_LEADING : run.leading,
  }
}

/**
 * Merge adjacent runs that share the same style into a single run (concatenating their text) so
 * the payload emits one `<TR>` per word/style span instead of one per character — matching how
 * yb360 stores shape text (e.g. `f`+`o`+`o` → `foo`). Style equality reuses the canonical
 * `runStyleEquals`/`extractRunStyle`, which compare only style fields (font, size, underline,
 * strikethrough, kerning, tracking, leading, lineStyle, fillStyle) and ignore the per-glyph wire
 * metrics `textAdvance`/`textWidth`/`baseline` — those are recomputed for the merged run by
 * `fillTextLineWireMetrics` (whole-run measurement → summed width). A run whose text contains a
 * newline is kept as its own boundary: yb360 stores `\n` as a standalone run and never folds it
 * into an adjacent word.
 */
export function coalesceWireRuns(runs: TextRun[]): TextRun[] {
  const out: TextRun[] = []

  for (const run of runs) {
    const prev = out[out.length - 1]
    const runHasNewline = (run.textContent ?? '').includes('\n')
    const prevHasNewline = (prev?.textContent ?? '').includes('\n')

    if (
      prev &&
      !runHasNewline &&
      !prevHasNewline &&
      runStyleEquals(extractRunStyle(prev), extractRunStyle(run))
    ) {
      prev.textContent = (prev.textContent ?? '') + (run.textContent ?? '')
    } else {
      out.push({ ...run })
    }
  }

  return out
}

export function buildTextLines(
  lines?: TextLine[],
  fonts?: ParagraphBlockFontLookup,
  coalesceRuns = false
): TextLine[] | undefined {
  if (!lines || lines.length === 0) return undefined
  return lines.map((line) => {
    const runs = line.textRuns?.map(ensureTextRunDefaults)
    const wireRuns = coalesceRuns && runs ? coalesceWireRuns(runs) : runs

    return fillTextLineWireMetrics({ ...line, textRuns: wireRuns }, fonts)
  })
}

/**
 * Build wire-ready paragraphs (per-run TA/BL/TW wire metrics filled in). `coalesceRuns` merges
 * adjacent same-style runs into one run before measuring — enabled for shape text (yb360 stores
 * shape text one `<TR>` per word/style span) and left off for standalone text frames, which
 * real yb360 stores one `<TR>` per character (see `Text_Example_NSD.xml` fixture / backend golden
 * round-trip test).
 */
export function buildWireParagraphs(
  paragraphs: Paragraph[],
  fonts?: ParagraphBlockFontLookup,
  coalesceRuns = false
): Paragraph[] {
  return paragraphs.map((p) => ({
    ...p,
    textLines: buildTextLines(p.textLines, fonts, coalesceRuns),
  }))
}
